<template>
  <div class="month-collections-report">
    <b-row class="content-header">
      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title float-left pr-1 mb-0">
              Collections Report
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-form @submit.prevent="generateReport">
      <b-card header-tag="header">
        <b-row>
          <b-col cols="md-12">
            <b-form-group
              label="Report Type"
              label-for="h-reportType"
              label-cols-md="2"
            >
              <b-form-radio-group
                id="h-reportType"
                v-model="reportType"
                button-variant="outline-primary"
                buttons
                class="spaceBetweenRadio"
              >
                <b-form-radio
                  v-for="(option, index) in reportOptions"
                  :key="index"
                  :value="option.value"
                >
                  <span class="align-middle">{{ option.text }}</span>
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </b-col>
          <b-col cols="md-12">
            <b-form-group
              label="Categories"
              label-for="h-categories"
              label-cols-md="2"
            >
              <v-select
                id="h-categories"
                v-model="selectedCategories"
                multiple
                :close-on-select="false"
                :options="categoryOptions"
              />
            </b-form-group>
          </b-col>
          <b-col cols="md-12">
            <b-form-group
              label="Date Range"
              label-cols-md="2"
            >
              <b-input-group>
                <flat-pickr
                  id="main-date"
                  v-model="dateRange"
                  class="form-control"
                  placeholder="Select a date range"
                  :config="flatPickrConfig"
                />
                <b-input-group-append is-text>
                  <feather-icon
                    icon="ChevronDownIcon"
                    class="cursor-pointer"
                    data-toggle
                    size="16"
                  />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col cols="md-12">
            <b-form-group
              label="Order Status"
              label-for="h-orderStatus"
              label-cols-md="2"
            >
              <b-form-tags
                v-model="orderStatus"
                size="lg"
                add-on-change
                no-outer-focus
              >
                <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                  <ul
                    v-if="tags.length > 0"
                    class="list-inline d-inline-block mb-1 user__lists"
                  >
                    <li
                      v-for="tag in tags"
                      :key="tag"
                      class="list-inline-item"
                    >
                      <b-form-tag
                        :title="tag"
                        :disabled="disabled"
                        variant="danger"
                        @remove="removeTag(tag)"
                      >
                        {{ tag }}
                      </b-form-tag>
                    </li>
                  </ul>
                  <b-form-select
                    v-bind="inputAttrs"
                    v-on="inputHandlers"
                  >
                    <template v-slot:first>
                      <!-- This is required to prevent bugs with Safari -->
                      <option
                        disabled
                        value=""
                      >
                        Choose status(s)...
                      </option>
                    </template>
                    <option
                      v-for="(opt, ind) in orderStatusOptions"
                      :key="ind"
                      :value="opt"
                    >
                      {{ opt }}
                    </option>
                  </b-form-select>
                </template>
              </b-form-tags>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="d-flex justify-content-end align-items-center flex-wrap"
          >
            <b-form-checkbox
              v-model="includeProductBreakdown"
              class="custom-control-primary mr-2 mb-0"
            >
              Include product breakdown
            </b-form-checkbox>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="primary"
              :disabled="loadingReport"
              @click="generateReport"
            >
              <span class="align-middle">{{ loadingReport ? 'Generating…' : 'Generate report' }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-form>

    <!-- Report -->
    <b-card
      v-if="showReport"
      header-tag="header"
      class="chart-card report__card"
    >
      <template #header>
        <div>
          <h3 class="align-middle mb-0">
            Monthly Collection
          </h3>
          <small class="text-muted">{{ reportPeriodLabel }}</small>
        </div>
        <b-button
          type="button"
          variant="flat-primary"
          class="float-right"
          @click="downloadCSV"
        >
          <feather-icon
            icon="DownloadIcon"
            class="mr-50"
            size="20"
          />
          <span class="">Download Excel</span>
        </b-button>
      </template>

      <!-- Stat tiles -->
      <div class="state_card">
        <b-row class="match-height">
          <b-col
            v-for="(tile, idx) in statTiles"
            :key="idx"
            md="3"
            sm="6"
            cols="12"
          >
            <b-card
              no-body
              class="stat-tile-card"
            >
              <b-card-body class="d-flex align-items-center">
                <img
                  :src="tile.image"
                  class="stat-tile-icon mr-1"
                  alt=""
                >
                <div class="truncate">
                  <h2 class="mb-0 font-weight-bolder">
                    {{ tile.value }}
                  </h2>
                  <span>{{ tile.label }}</span>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Data table -->
      <div class="table-responsive month-collections-table-wrap">
        <table
          id="monthCollectionsTable"
          class="month-collections-table"
        >
          <thead>
            <tr>
              <th
                rowspan="2"
                class="category-col"
              >
                Category/Product
              </th>
              <th
                v-for="bucket in buckets"
                :key="bucket.key"
                colspan="2"
                class="group-col"
              >
                {{ bucket.label }}
              </th>
              <th
                colspan="2"
                class="group-col total-col"
              >
                TOTAL {{ currentLabel }}
              </th>
              <th
                colspan="2"
                class="group-col"
              >
                PRIOR YEAR {{ priorLabel }}
              </th>
              <th
                rowspan="2"
                class="yty-col"
              >
                YTY<br>AMT
              </th>
            </tr>
            <tr>
              <template v-for="bucket in buckets">
                <th
                  :key="`${bucket.key}-qty`"
                  class="text-right"
                >
                  Qty
                </th>
                <th
                  :key="`${bucket.key}-amt`"
                  class="text-right"
                >
                  Amt(S$)
                </th>
              </template>
              <th class="total-col">
                Qty
              </th>
              <th class="total-col">
                Amt(S$)
              </th>
              <th>Qty</th>
              <th>Amt(S$)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in tableRows"
              :key="row.key"
              :class="row.isChild ? 'child-row' : 'category-row'"
            >
              <td>
                <span
                  v-if="!row.isChild && includeProductBreakdown"
                  class="expand-toggle"
                  :class="{ 'expand-toggle--disabled': !row.children }"
                  @click="toggleCategory(row)"
                >
                  <feather-icon
                    :icon="expanded[row.key] ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                    size="14"
                  />
                </span>
                {{ row.label }}
              </td>
              <template v-for="bucket in buckets">
                <td :key="`${bucket.key}-qty`">
                  {{ formatQty(row[bucket.key].qty) }}
                </td>
                <td :key="`${bucket.key}-amt`">
                  {{ formatAmt(row[bucket.key].amt) }}
                </td>
              </template>
              <td class="total-col">
                {{ formatQty(row.total.qty) }}
              </td>
              <td class="total-col">
                {{ formatAmt(row.total.amt) }}
              </td>
              <td>{{ formatQty(row.prior.qty) }}</td>
              <td>{{ formatAmt(row.prior.amt) }}</td>
              <td :class="ytyClass(row.yty)">
                {{ formatYty(row.yty) }}
              </td>
            </tr>
            <tr
              v-if="grandTotal"
              class="grand-total-row"
            >
              <td>Grand Total</td>
              <template v-for="bucket in buckets">
                <td :key="`${bucket.key}-qty`">
                  {{ formatQty(grandTotal[bucket.key].qty) }}
                </td>
                <td :key="`${bucket.key}-amt`">
                  {{ formatAmt(grandTotal[bucket.key].amt) }}
                </td>
              </template>
              <td class="total-col">
                {{ formatQty(grandTotal.total.qty) }}
              </td>
              <td class="total-col">
                {{ formatAmt(grandTotal.total.amt) }}
              </td>
              <td>{{ formatQty(grandTotal.prior.qty) }}</td>
              <td>{{ formatAmt(grandTotal.prior.amt) }}</td>
              <td :class="ytyClass(grandTotal.yty)">
                {{ formatYty(grandTotal.yty) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Charts -->
      <div class="chats__block mt-2">
        <b-row class="match-height">
          <b-col
            md="6"
            cols="12"
          >
            <chartjs-bar-chart
              :title="categoryYoyChartTitle"
              :year-bar-chart-data="categoryYoyBarChartData"
              :y-axis-tick-formatter="formatChartAxisCurrency"
            />
          </b-col>
          <b-col
            md="6"
            cols="12"
          >
            <b-card class="card shadow-body">
              <b-card-header class="report-full-header">
                <b-card-title class="mb-0">
                  Year-on-Year Change by Category (%)
                </b-card-title>
                <div class="custom-legend">
                  <span><span class="legend-dot legend-growth" />Growth</span>
                  <span><span class="legend-dot legend-decline" />Decline</span>
                </div>
              </b-card-header>
              <b-card-body>
                <chartjs-component-bar-chart
                  :height="400"
                  :data="yoyChangeChartData"
                  :options="yoyChangeChartOptions"
                />
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BCardHeader, BCardTitle, BRow, BCol, BForm, BButton, BFormTags, BFormTag, BFormSelect,
  BFormGroup, BFormRadio, BFormRadioGroup, BFormCheckbox, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import '@core/scss/vue/libs/vue-flatpicker.scss'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import ChartjsBarChart from './chartjs/ChartjsBarChart.vue'
import ChartjsComponentBarChart from './chartjs/charts-components/ChartjsComponentBarChart.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BRow,
    BCol,
    BForm,
    BButton,
    BFormTags,
    BFormTag,
    BFormSelect,
    BFormGroup,
    BFormRadio,
    BFormRadioGroup,
    BFormCheckbox,
    BInputGroup,
    BInputGroupAppend,

    vSelect,
    flatPickr,

    // charts
    ChartjsBarChart,
    ChartjsComponentBarChart,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      showReport: false,
      loadingReport: false,
      reportType: 'month',
      reportOptions: [
        { text: 'By months (With comparison)', value: 'month' },
        { text: 'By years (With comparison)', value: 'year' },
      ],
      categoryOptions: ['All'],
      selectedCategories: ['All'],
      dateRange: '',
      flatPickrConfig: {
        wrap: true, mode: 'range', dateFormat: 'd/m/Y', enableTime: false, disableMobile: true,
      },
      orderStatusOptions: ['Pending', 'Completed'],
      orderStatus: ['Pending', 'Completed'],
      includeProductBreakdown: true,
      reportPeriodLabel: '',
      currentLabel: '',
      priorLabel: '',
      dateRangeISO: null,
      categoryYoyChartTitle: "Collections by Category – Year on Year ($'000)",
      expanded: {},
      buckets: [],
      categories: [],
      grandTotal: null,
      categoryYoyBarChartData: { labels: [], datasets: [] },
      yoyChangeChartData: { labels: [], datasets: [] },
      yoyChangeChartOptions: {
        elements: {
          rectangle: {
            borderWidth: 1,
            borderSkipped: 'true',
            borderRadius: 0,
          },
          borderRadius: 0,
        },
        responsive: true,
        maintainAspectRatio: false,
        responsiveAnimationDuration: 500,
        legend: {
          display: false,
        },
        tooltips: {
          callbacks: {
            label(tooltipItem) {
              return `${tooltipItem.yLabel}%`
            },
          },
          shadowOffsetX: 1,
          shadowOffsetY: 1,
          shadowBlur: 8,
          shadowColor: 'rgba(0, 0, 0, 0.25)',
        },
        scales: {
          xAxes: [
            {
              display: true,
              gridLines: {
                display: true,
                color: 'rgba(200, 200, 200, 0.2)',
                zeroLineColor: 'rgba(200, 200, 200, 0.2)',
              },
              ticks: {
                fontColor: '#000',
                fontWeight: 600,
              },
            },
          ],
          yAxes: [
            {
              display: true,
              gridLines: {
                color: 'rgba(200, 200, 200, 0.2)',
                zeroLineColor: 'rgba(200, 200, 200, 0.2)',
              },
              ticks: {
                min: -80,
                max: 100,
                stepSize: 20,
                callback: value => `${value}%`,
                fontColor: '#000',
                fontWeight: 600,
              },
            },
          ],
        },
      },
    }
  },
  computed: {
    tableRows() {
      const rows = []
      this.categories.forEach(category => {
        rows.push({ ...category, isChild: false })
        if (this.includeProductBreakdown && category.children && this.expanded[category.key]) {
          category.children.forEach(child => {
            rows.push({ ...child, isChild: true })
          })
        }
      })
      return rows
    },
    statTiles() {
      return [
        {
          // eslint-disable-next-line global-require
          image: require('@/assets/images/icons/reports/total-collected.svg'),
          value: `S$${Math.round(this.grandTotal.total.amt).toLocaleString('en-US')}`,
          label: `Total Collected (${this.currentLabel})`,
        },
        {
          // eslint-disable-next-line global-require
          image: require('@/assets/images/icons/reports/prior-year.svg'),
          value: `S$${Math.round(this.grandTotal.prior.amt).toLocaleString('en-US')}`,
          label: 'Prior Year (Same Period)',
        },
        {
          // eslint-disable-next-line global-require
          image: require('@/assets/images/icons/reports/yty-change.svg'),
          value: this.formatYty(this.grandTotal.yty),
          label: 'YTY Change',
        },
        {
          // eslint-disable-next-line global-require
          image: require('@/assets/images/icons/reports/total-orders.svg'),
          value: this.grandTotal.total.qty.toLocaleString('en-US'),
          label: 'Total Orders (Across All Categories)',
        },
      ]
    },
  },
  beforeMount() {
    this.fetchCategoryOptions()
  },
  methods: {
    toggleCategory(row) {
      if (!row.children) return
      this.$set(this.expanded, row.key, !this.expanded[row.key])
    },
    formatQty(value) {
      return Number(value).toLocaleString('en-US')
    },
    formatAmt(value) {
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatYty(value) {
      if (value === null || value === undefined) return 'N/A'
      const sign = value >= 0 ? '+' : ''
      return `${sign}${value.toFixed(1)}%`
    },
    ytyClass(value) {
      if (value === null || value === undefined) return ''
      return value >= 0 ? 'text-success' : 'text-danger'
    },
    formatChartAxisCurrency(value) {
      return `$${Number(value).toLocaleString('en-US')}k`
    },
    fetchCategoryOptions() {
      this.$http.get('operation/reports/collections/extra-options')
        .then(response => {
          this.categoryOptions = ['All', ...(response.data.categoryOptions ?? [])]
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    yearsInRange(dateRange) {
      const splitted = dateRange.split(' ')
      const startYear = Number(splitted[0].split('/').pop())
      const endYear = Number((splitted[2] ?? splitted[0]).split('/').pop())
      const years = []
      for (let year = startYear; year <= endYear; year += 1) {
        years.push(year)
      }
      return years
    },
    generateReport() {
      if (!this.dateRange) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please select a date range to generate the report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const formData = new FormData()
      const categories = this.selectedCategories.includes('All') ? [] : this.selectedCategories
      formData.append('categories', JSON.stringify(categories))
      formData.append('orderStatus', JSON.stringify(this.orderStatus))

      let url = 'operation/reports/generate/collections/month'
      if (this.reportType === 'month') {
        formData.append('dateRange', this.dateRange)
      } else {
        url = 'operation/reports/generate/collections/year'
        formData.append('year', JSON.stringify(this.yearsInRange(this.dateRange)))
      }

      this.loadingReport = true
      this.$http.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(response => {
          const { data } = response
          this.buckets = data.buckets
          this.categories = data.categories
          this.grandTotal = data.grandTotal
          this.categoryYoyBarChartData = data.categoryYoyChartData
          this.yoyChangeChartData = data.yoyChangeChartData
          this.reportPeriodLabel = data.periodLabel
          this.currentLabel = data.currentLabel
          this.priorLabel = data.priorLabel
          this.dateRangeISO = data.dateRangeISO
          this.expanded = {}
          this.showReport = true
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loadingReport = false
        })
    },
    csvCell(value) {
      const text = String(value ?? '')
      return `"${text.replace(/"/g, '""')}"`
    },
    downloadCSV() {
      if (!this.grandTotal) return

      const header = ['Level', 'Category', 'Product']
      this.buckets.forEach(bucket => {
        header.push(`${bucket.label} Qty`, `${bucket.label} Amt ($)`)
      })
      header.push(`Total ${this.currentLabel} Qty`, `Total ${this.currentLabel} Amt ($)`, 'Prior Year Qty', 'Prior Year Amt ($)', 'YTY Amt (%)')

      const roundAmt = value => Number(value).toFixed(2)

      const buildRow = (level, category, product, row) => {
        const cells = [level, category, product]
        this.buckets.forEach(bucket => {
          cells.push(row[bucket.key].qty, roundAmt(row[bucket.key].amt))
        })
        cells.push(row.total.qty, roundAmt(row.total.amt), row.prior.qty, roundAmt(row.prior.amt), row.yty === null ? '' : row.yty.toFixed(1))
        return cells
      }

      const rows = [header]
      this.categories.forEach(category => {
        rows.push(buildRow('Category', category.label, '', category))
        category.children.forEach(product => {
          rows.push(buildRow('Product', category.label, product.label, product))
        })
      })
      rows.push(buildRow('Grand Total', 'Grand Total', '', this.grandTotal))

      const csvString = rows.map(row => row.map(this.csvCell).join(',')).join('\n')
      const start = this.dateRangeISO ? this.dateRangeISO.start : ''
      const end = this.dateRangeISO ? this.dateRangeISO.end : ''
      const filename = `monthly_collections_${start}_to_${end}.csv`

      const link = document.createElement('a')
      link.style.display = 'none'
      link.setAttribute('target', '_blank')
      link.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(csvString)}`)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style lang="scss" scoped>
.stat-tile-card {
  border: 1px solid #ebe9f1;
  box-shadow: none;
}

.stat-tile-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

// Bootstrap's default "active" state on an outline-primary button-group label is a pale
// tint, not a solid fill - other pages opt into the solid dark-blue "selected pill" look
// with a page-scoped override (see .service-description-fields_group in
// _phase5aDesign.scss); this page needs the same treatment for its Report Type toggle.
::v-deep .spaceBetweenRadio label.btn-outline-primary.active {
  background-color: #104D9D !important;
  border-color: #104D9D !important;
  color: #fff !important;
}

// _purchase.scss defines a global, unscoped ".card-header small" rule (uppercase, blue,
// smaller line-height) meant for its own pages, which leaks into every other card header's
// <small> in the app - including this report's period label. Override it back to the
// plain muted, mixed-case text the design calls for.
.report__card ::v-deep .card-header small.text-muted {
  display: inline;
  text-transform: none;
  color: #6e6b7b !important;
  font-size: inherit;
  line-height: inherit;
}

.month-collections-table-wrap {
  overflow-x: auto;
}

.month-collections-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.7rem 0.9rem;
    border: none;
    white-space: nowrap;
    text-align: right;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  thead {
    th {
      background-color: #eef1f7;
      text-transform: uppercase;
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      color: #6e6b7b;
    }

    tr:first-child th {
      padding-bottom: 0.4rem;
    }

    tr:last-child th {
      padding-top: 0.3rem;
      border-bottom: 1px solid #dcdfe6;
    }

    th[rowspan] {
      vertical-align: middle;
      border-bottom: 1px solid #dcdfe6;
    }

    .group-col {
      text-align: center;
    }

    .yty-col {
      text-align: right;
    }
  }

  tbody td {
    border-bottom: 1px solid #f0f0f3;
  }

  .total-col {
    background-color: #eaf0fb;
  }

  thead .total-col {
    background-color: #d9e4f6;
  }

  .category-row td:first-child {
    font-weight: 600;
    color: #171822;
  }

  .child-row td {
    color: #6e6b7b;
  }

  .child-row td:first-child {
    padding-left: 2.5rem;
  }

  .grand-total-row td {
    font-weight: 700;
    color: #171822;
    border-top: 2px solid #d8d6de;
    border-bottom: none;
    background-color: #f8f9fc;
  }

  .grand-total-row .total-col {
    background-color: #dbe6f7;
  }

  .expand-toggle {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    margin-right: 0.35rem;
    vertical-align: middle;

    &--disabled {
      cursor: default;
    }
  }
}

.custom-legend {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  color: #6e6b7b;

  span + span {
    margin-left: 1rem;
  }

  .legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 0.35rem;
  }

  .legend-growth {
    background-color: #28c76f;
  }

  .legend-decline {
    background-color: #ea5455;
  }
}
</style>
